import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import {
  mockCandidates,
  mockJobs,
  type Candidate,
} from "@project/data/mockData";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Search,
  Send,
  FileText,
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  User,
  Mail,
  Briefcase,
  TrendingUp,
  IndianRupee,
  Brain,
  BadgeIndianRupee,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

interface Offer {
  id: string;
  candidateId: string;
  candidateName: string;
  candidateAvatar: string;
  jobTitle: string;
  department: string;
  salaryOffered: string;
  salaryExpected: string;
  status:
    | "Draft"
    | "Sent"
    | "Accepted"
    | "Rejected"
    | "Negotiation"
    | "Expired";
  sentDate: string;
  responseDate?: string;
  notes: string;
}

const mockOffers: Offer[] = [
  {
    id: "o1",
    candidateId: "c4",
    candidateName: "Vikram Singh",
    candidateAvatar: "VS",
    jobTitle: "Senior UX Designer",
    department: "Design",
    salaryOffered: "₹32 LPA",
    salaryExpected: "₹35 LPA",
    status: "Accepted",
    sentDate: "2026-03-20",
    responseDate: "2026-03-22",
    notes: "Candidate accepted after salary revision.",
  },
  {
    id: "o2",
    candidateId: "c1",
    candidateName: "Priya Sharma",
    candidateAvatar: "PS",
    jobTitle: "Senior UX Designer",
    department: "Design",
    salaryOffered: "₹30 LPA",
    salaryExpected: "₹28 LPA",
    status: "Sent",
    sentDate: "2026-03-25",
    notes: "Awaiting response.",
  },
  {
    id: "o3",
    candidateId: "c3",
    candidateName: "Anita Desai",
    candidateAvatar: "AD",
    jobTitle: "Financial Analyst",
    department: "Finance",
    salaryOffered: "₹16 LPA",
    salaryExpected: "₹18 LPA",
    status: "Negotiation",
    sentDate: "2026-03-22",
    notes: "Candidate requested higher CTC.",
  },
];

const statusConfig: Record<string, { icon: any; class: string }> = {
  Draft: { icon: FileText, class: "text-muted-foreground bg-muted" },
  Sent: {
    icon: Send,
    class: "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  },
  Accepted: {
    icon: CheckCircle2,
    class: "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  },
  Rejected: {
    icon: XCircle,
    class: "text-destructive bg-[hsl(var(--status-filled-bg))]",
  },
  Negotiation: {
    icon: TrendingUp,
    class: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  },
  Expired: {
    icon: Clock,
    class: "text-[hsl(var(--status-closed))] bg-[hsl(var(--status-closed-bg))]",
  },
};

const OffersPage = () => {
  const [offers, setOffers] = useState(mockOffers);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [createOpen, setCreateOpen] = useState(false);
  const [aiLetterOpen, setAiLetterOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLetter, setAiLetter] = useState("");
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [aiSalaryOpen, setAiSalaryOpen] = useState(false);
  const aiSalary = useAIAction();
  // New offer form
  const [newOffer, setNewOffer] = useState({
    candidateId: "",
    salaryOffered: "",
    notes: "",
  });

  const tabs = [
    { label: "All", count: offers.length },
    { label: "Sent", count: offers.filter(o => o.status === "Sent").length },
    {
      label: "Negotiation",
      count: offers.filter(o => o.status === "Negotiation").length,
    },
    {
      label: "Accepted",
      count: offers.filter(o => o.status === "Accepted").length,
    },
    {
      label: "Rejected",
      count: offers.filter(o => o.status === "Rejected").length,
    },
  ];

  const filtered = (
    activeTab === "All" ? offers : offers.filter(o => o.status === activeTab)
  ).filter(
    o => !search || o.candidateName.toLowerCase().includes(search.toLowerCase())
  );

  const eligibleCandidates = mockCandidates.filter(c =>
    ["Offered", "Shortlisted", "Interview"].includes(c.stage)
  );

  const handleCreateOffer = () => {
    const candidate = mockCandidates.find(c => c.id === newOffer.candidateId);
    if (!candidate) {
      toast({ title: "Select a candidate", variant: "destructive" });
      return;
    }
    const job = mockJobs.find(j => j.id === candidate.jobId);
    const offer: Offer = {
      id: `o${Date.now()}`,
      candidateId: candidate.id,
      candidateName: candidate.name,
      candidateAvatar: candidate.avatar,
      jobTitle: candidate.jobTitle,
      department: job?.department || "General",
      salaryOffered: newOffer.salaryOffered || "TBD",
      salaryExpected: "TBD",
      status: "Draft",
      sentDate: new Date().toISOString().split("T")[0],
      notes: newOffer.notes,
    };
    setOffers(prev => [offer, ...prev]);
    setCreateOpen(false);
    setNewOffer({ candidateId: "", salaryOffered: "", notes: "" });
    toast({
      title: "Offer Created",
      description: `Offer for ${candidate.name} saved as Draft.`,
    });
  };

  const handleSendOffer = (id: string) => {
    setOffers(prev =>
      prev.map(o =>
        o.id === id
          ? {
              ...o,
              status: "Sent" as const,
              sentDate: new Date().toISOString().split("T")[0],
            }
          : o
      )
    );
    toast({ title: "Offer Sent" });
  };

  const handleGenerateOfferLetter = async (offer: Offer) => {
    toast({
      title: "Feature Disabled",
      description:
        "AI Offer Letter generation is currently unavailable because Supabase has been removed.",
      variant: "destructive",
    });
  };

  const stats = [
    {
      label: "Total Offers",
      value: offers.length,
      icon: FileText,
      accent: "hsl(var(--primary))",
      bg: "hsl(var(--primary) / 0.08)",
    },
    {
      label: "Accepted",
      value: offers.filter(o => o.status === "Accepted").length,
      icon: CheckCircle2,
      accent: "hsl(160 84% 39%)",
      bg: "hsl(160 84% 39% / 0.08)",
    },
    {
      label: "In Negotiation",
      value: offers.filter(o => o.status === "Negotiation").length,
      icon: TrendingUp,
      accent: "hsl(38 92% 50%)",
      bg: "hsl(38 92% 50% / 0.08)",
    },
    {
      label: "Pending",
      value: offers.filter(o => o.status === "Sent").length,
      icon: Clock,
      accent: "hsl(199 89% 48%)",
      bg: "hsl(199 89% 48% / 0.08)",
    },
  ];

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Offers
            </h1>
            <p className="mt-0.5 hidden text-[12px] text-muted-foreground sm:block">
              Manage offers, negotiations, and acceptance tracking
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                setAiSalaryOpen(true);
                await aiSalary.run(`You are a compensation analyst AI. Analyze these offer details and provide salary recommendations:

${offers.map(o => `- ${o.candidateName}: Position=${o.jobTitle}, Department=${o.department}, Offered=${o.salaryOffered}, Expected=${o.salaryExpected}, Status=${o.status}`).join("\n")}

Provide:
1. **Market Analysis** - how the offers compare to market rates (Indian market)
2. **Salary Gap Analysis** - offered vs expected for each candidate
3. **Negotiation Strategy** - tips for candidates in negotiation
4. **Budget Optimization** - how to balance quality hires with budget
5. **Recommendations** - specific salary adjustments with reasoning`);
              }}
            >
              <Brain className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Salary Advisor</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="btn-glow h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
            >
              <Send className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Create Offer</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map(s => (
            <motion.div
              key={s.label}
              variants={item}
              className="section-card p-3 sm:p-4"
            >
              <div className="mb-2 flex items-center gap-2">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{ background: s.bg }}
                >
                  <s.icon className="h-3.5 w-3.5" style={{ color: s.accent }} />
                </div>
              </div>
              <p className="font-display text-xl font-bold text-foreground">
                {s.value}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {tabs.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab.label)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${activeTab === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
              <span
                className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${activeTab === tab.label ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Search */}
        <motion.div variants={item}>
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search offers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
        </motion.div>

        {/* Table */}
        <motion.div variants={item}>
          {filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <Send className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">
                No offers found
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="space-y-2 sm:hidden">
                {filtered.map(o => {
                  const sc = statusConfig[o.status];
                  return (
                    <div key={o.id} className="section-card space-y-2 p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                          <span className="text-[10px] font-bold text-white">
                            {o.candidateAvatar}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-foreground">
                            {o.candidateName}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {o.jobTitle}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-semibold ${sc.class}`}
                        >
                          <sc.icon className="h-3 w-3" />
                          {o.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <IndianRupee className="h-3 w-3" />
                          {o.salaryOffered}
                        </span>
                        <div className="flex gap-1">
                          {o.status === "Draft" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleSendOffer(o.id)}
                            >
                              Send
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="ai-btn-outline h-6 gap-1 rounded-lg px-2 text-[10px]"
                            onClick={() => handleGenerateOfferLetter(o)}
                          >
                            <Sparkles className="h-3 w-3" />
                            AI Letter
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table */}
              <div className="section-card hidden overflow-hidden sm:block">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="pl-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Candidate
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Position
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Offered
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Expected
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Status
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Sent
                        </TableHead>
                        <TableHead className="w-[140px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map(o => {
                        const sc = statusConfig[o.status];
                        return (
                          <TableRow key={o.id} className="table-row-hover">
                            <TableCell className="pl-5">
                              <div className="flex items-center gap-2.5">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                                  <span className="text-[9px] font-bold text-white">
                                    {o.candidateAvatar}
                                  </span>
                                </div>
                                <p className="text-[12px] font-semibold text-foreground">
                                  {o.candidateName}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="text-[12px] text-foreground">
                              {o.jobTitle}
                            </TableCell>
                            <TableCell className="text-[12px] font-semibold text-foreground">
                              {o.salaryOffered}
                            </TableCell>
                            <TableCell className="text-[12px] text-muted-foreground">
                              {o.salaryExpected}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold ${sc.class}`}
                              >
                                <sc.icon className="h-3 w-3" />
                                {o.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-[12px] text-muted-foreground">
                              {o.sentDate}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {o.status === "Draft" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[11px]"
                                    onClick={() => handleSendOffer(o.id)}
                                  >
                                    <Send className="mr-1 h-3 w-3" />
                                    Send
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  className="ai-btn-outline h-7 gap-1 rounded-lg px-2 text-[11px]"
                                  onClick={() => handleGenerateOfferLetter(o)}
                                >
                                  <Sparkles className="h-3 w-3" />
                                  AI Letter
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>

      {/* Create Offer Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Create Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[12px]">Candidate *</Label>
              <Select
                value={newOffer.candidateId}
                onValueChange={v =>
                  setNewOffer(p => ({ ...p, candidateId: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select candidate" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleCandidates.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — {c.jobTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Salary Offered</Label>
              <Input
                value={newOffer.salaryOffered}
                onChange={e =>
                  setNewOffer(p => ({ ...p, salaryOffered: e.target.value }))
                }
                placeholder="e.g. ₹30 LPA"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Notes</Label>
              <Textarea
                value={newOffer.notes}
                onChange={e =>
                  setNewOffer(p => ({ ...p, notes: e.target.value }))
                }
                rows={3}
                placeholder="Additional details..."
              />
            </div>
            <Button onClick={handleCreateOffer} className="btn-glow w-full">
              Create Offer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Offer Letter Dialog */}
      <Dialog open={aiLetterOpen} onOpenChange={setAiLetterOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" />
              AI-Generated Offer Letter
              {selectedOffer && (
                <span className="text-[12px] font-normal text-muted-foreground">
                  — {selectedOffer.candidateName}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Generating offer letter...
              </p>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="whitespace-pre-wrap rounded-xl bg-muted/30 p-4 font-mono text-[13px] leading-relaxed text-foreground">
                {aiLetter}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    navigator.clipboard.writeText(aiLetter);
                    toast({ title: "Copied!" });
                  }}
                >
                  Copy to Clipboard
                </Button>
                <Button
                  className="btn-glow flex-1"
                  onClick={() => {
                    toast({ title: "Offer letter sent via email!" });
                    setAiLetterOpen(false);
                  }}
                >
                  <Mail className="mr-1.5 h-3.5 w-3.5" />
                  Send via Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Salary Advisor Dialog */}
      <Dialog open={aiSalaryOpen} onOpenChange={setAiSalaryOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Brain className="h-5 w-5 text-primary" />
              AI Salary Advisor
            </DialogTitle>
          </DialogHeader>
          {aiSalary.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Analyzing compensation data...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiSalary.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default OffersPage;
